import { useEffect, useMemo, useState } from 'react'
import { Building2, Plus, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import ConfirmModal from '../ConfirmModal'
import { CrmSetupNotice, useCrmReferenceData } from '../lib/useCrmReferenceData'

const EMPTY_FORM = {
  business_id: '', business_ids: [], name: '', website: '', email_domain: '', industry: '', country: '', owner_id: ''
}

function normaliseDomain(value) {
  return value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
}

function normaliseName(value) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function getCompanyCreateError(error) {
  if (error?.code === '23505') return error.message
  if (error?.code === '42501' || /permission|row-level security/i.test(error?.message || '')) {
    return 'You do not have permission to add companies in this CRM workspace'
  }
  if (/owner must belong/i.test(error?.message || '')) {
    return 'The selected owner does not belong to this CRM workspace organisation'
  }
  if (/business must belong/i.test(error?.message || '')) {
    return 'The selected Business does not belong to this CRM workspace'
  }
  return error?.message || 'Failed to add company'
}

export default function Companies() {
  const toast = useToast()
  const navigate = useNavigate()
  const {
    user, activeCrmWorkspace, activeCrmWorkspaceId, businesses, profiles,
    canManageWorkspace, canManageRecords, loadingReferences, refreshReferences
  } = useCrmReferenceData()
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showBusinessForm, setShowBusinessForm] = useState(false)
  const [businessName, setBusinessName] = useState('')
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [businessFilter, setBusinessFilter] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    const defaultOwnerId = profiles.some(profile => profile.id === user?.id)
      ? user.id
      : profiles[0]?.id || ''
    setForm(current => ({
      ...EMPTY_FORM,
      business_id: businesses[0]?.id || '',
      business_ids: businesses[0]?.id ? [businesses[0].id] : [],
      owner_id: defaultOwnerId
    }))
  }, [activeCrmWorkspaceId, businesses, profiles, user?.id])

  useEffect(() => {
    fetchCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrmWorkspaceId])

  async function fetchCompanies() {
    if (!activeCrmWorkspaceId) {
      setCompanies([])
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await supabase
      .from('crm_companies')
      .select('*, crm_businesses!crm_companies_business_id_fkey(name), crm_company_businesses(business_id, crm_businesses(name)), profiles!crm_companies_owner_id_fkey(full_name, email)')
      .eq('workspace_id', activeCrmWorkspaceId)
      .order('created_at', { ascending: false })
    if (error) toast.error('Failed to load companies')
    setCompanies(data || [])
    setLoading(false)
  }

  async function addBusiness() {
    if (!businessName.trim()) return toast.error('Business name is required')
    setSaving(true)
    const { error } = await supabase.from('crm_businesses').insert({
      workspace_id: activeCrmWorkspaceId,
      name: businessName.trim(),
      created_by: user.id
    })
    setSaving(false)
    if (error) return toast.error(error.code === '23505' ? 'That Business already exists' : 'Failed to add Business')
    setBusinessName('')
    setShowBusinessForm(false)
    toast.success('Business added with its default pipeline')
    refreshReferences()
  }

  async function addCompany() {
    if (!form.name.trim() || !form.business_id || !form.owner_id) {
      return toast.error('Company, Business and owner are required')
    }
    if (!businesses.some(business => business.id === form.business_id)) {
      return toast.error('Select a Business from the current CRM workspace')
    }
    if (!profiles.some(profile => profile.id === form.owner_id)) {
      return toast.error('Select an owner from the current organisation')
    }
    if (companies.some(company => normaliseName(company.name) === normaliseName(form.name))) {
      return toast.error('A company with this name already exists')
    }
    setSaving(true)
    const selectedBusinessIds = [...new Set([form.business_id, ...form.business_ids])]
      .filter(businessId => businesses.some(business => business.id === businessId))
    const { error } = await supabase.rpc('create_crm_company', {
      target_workspace_id: activeCrmWorkspaceId,
      target_primary_business_id: form.business_id,
      target_business_ids: selectedBusinessIds,
      target_owner_id: form.owner_id,
      company_name: form.name.trim(),
      company_website: form.website.trim() || null,
      company_email_domain: form.email_domain ? normaliseDomain(form.email_domain) : null,
      company_industry: form.industry.trim() || null,
      company_country: form.country.trim() || null
    })
    if (error) {
      console.error('Failed to add CRM company:', error)
      setSaving(false)
      return toast.error(getCompanyCreateError(error))
    }
    setSaving(false)
    toast.success('Company added')
    setShowForm(false)
    const defaultOwnerId = profiles.some(profile => profile.id === user?.id) ? user.id : profiles[0]?.id || ''
    setForm({ ...EMPTY_FORM, business_id: businesses[0]?.id || '', business_ids: businesses[0]?.id ? [businesses[0].id] : [], owner_id: defaultOwnerId })
    fetchCompanies()
  }

  async function deleteCompany() {
    const { error } = await supabase.from('crm_companies').delete().eq('id', confirmDelete)
    if (error) toast.error('Company cannot be removed while it has linked records')
    else toast.success('Company removed')
    setConfirmDelete(null)
    fetchCompanies()
  }

  const filtered = useMemo(() => companies.filter(company => {
    const matchesSearch = [company.name, company.email_domain, company.industry]
      .some(value => (value || '').toLowerCase().includes(search.toLowerCase()))
    return matchesSearch && (!businessFilter || company.crm_company_businesses?.some(link => link.business_id === businessFilter))
  }), [companies, search, businessFilter])

  return (
    <div className="hv-page">
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Companies</h1>
          <p className="hv-page-subtitle">Organisations and brands in {activeCrmWorkspace?.name || 'your CRM workspace'}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canManageWorkspace && activeCrmWorkspaceId && (
            <button className="hv-btn hv-btn-secondary" onClick={() => setShowBusinessForm(value => !value)}>
              <Building2 size={15} /> New Business
            </button>
          )}
          {canManageRecords && <button className="hv-btn hv-btn-primary" disabled={!businesses.length} onClick={() => setShowForm(value => !value)}>
            <Plus size={15} /> Add Company
          </button>}
        </div>
      </div>

      {showBusinessForm && (
        <div className="hv-card crm-quick-form">
          <div>
            <label className="hv-form-label">Business Name</label>
            <input className="hv-input" value={businessName} onChange={event => setBusinessName(event.target.value)} placeholder="e.g. Playmakers" autoFocus />
          </div>
          <button className="hv-btn hv-btn-primary" onClick={addBusiness} disabled={saving}>Create Business</button>
        </div>
      )}

      {showForm && (
        <div className="hv-card crm-record-form">
          <div className="crm-form-grid">
            <Field label="Company Name *"><input className="hv-input" value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} /></Field>
            <Field label="Businesses *"><div className="crm-business-checks">{businesses.map(business => <label key={business.id}><input type="checkbox" checked={form.business_ids.includes(business.id)} onChange={() => { const business_ids = form.business_ids.includes(business.id) ? form.business_ids.filter(id => id !== business.id) : [...form.business_ids, business.id]; setForm({ ...form, business_ids, business_id: business_ids.includes(form.business_id) ? form.business_id : business_ids[0] || '' }) }} /> {business.name}</label>)}</div></Field>
            <Field label="Website"><input className="hv-input" value={form.website} onChange={event => setForm({ ...form, website: event.target.value })} placeholder="https://" /></Field>
            <Field label="Email Domain"><input className="hv-input" value={form.email_domain} onChange={event => setForm({ ...form, email_domain: event.target.value })} placeholder="example.com" /></Field>
            <Field label="Industry"><input className="hv-input" value={form.industry} onChange={event => setForm({ ...form, industry: event.target.value })} /></Field>
            <Field label="Country"><input className="hv-input" value={form.country} onChange={event => setForm({ ...form, country: event.target.value })} /></Field>
            <Field label="Owner *"><select className="hv-select" value={form.owner_id} onChange={event => setForm({ ...form, owner_id: event.target.value })}>{profiles.map(profile => <option key={profile.id} value={profile.id}>{profile.full_name || profile.email}</option>)}</select></Field>
          </div>
          <div className="crm-form-actions"><button className="hv-btn hv-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button><button className="hv-btn hv-btn-primary" onClick={addCompany} disabled={saving}>{saving ? 'Saving...' : 'Save Company'}</button></div>
        </div>
      )}

      {!loadingReferences && (!activeCrmWorkspaceId || !businesses.length) ? (
        <CrmSetupNotice hasWorkspace={!!activeCrmWorkspaceId} hasBusinesses={businesses.length > 0} />
      ) : (
        <>
          <div className="crm-toolbar">
            <div className="hv-search-wrap"><span className="hv-search-icon"><Search size={15} /></span><input className="hv-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search companies..." /></div>
            <select className="hv-select" value={businessFilter} onChange={event => setBusinessFilter(event.target.value)}><option value="">All Businesses</option>{businesses.map(business => <option key={business.id} value={business.id}>{business.name}</option>)}</select>
          </div>
          <div className="hv-table-wrap">
            {loading ? <div className="hv-empty-text" style={{ padding: 30 }}>Loading...</div> : filtered.length === 0 ? <div className="hv-empty"><div className="hv-empty-title">No companies found</div><div className="hv-empty-text">Add the first company for this workspace.</div></div> : (
              <table className="hv-table"><thead><tr><th>Company</th><th>Businesses</th><th>Industry</th><th>Country</th><th>Owner</th><th></th></tr></thead><tbody>{filtered.map(company => <tr key={company.id}><td><button className="crm-record-link" onClick={() => navigate(`/companies/${company.id}`)}><span>{company.name}</span><small>{company.email_domain || company.website || 'No domain'}</small></button></td><td>{company.crm_company_businesses?.map(link => link.crm_businesses?.name).filter(Boolean).join(', ') || company.crm_businesses?.name}</td><td>{company.industry || '—'}</td><td>{company.country || '—'}</td><td>{company.profiles?.full_name || company.profiles?.email}</td><td><div className="crm-row-actions"><button className="hv-btn hv-btn-secondary" onClick={() => navigate(`/companies/${company.id}`)}>View</button>{canManageRecords && <button className="hv-btn hv-btn-danger" onClick={() => setConfirmDelete(company.id)}>Delete</button>}</div></td></tr>)}</tbody></table>
            )}
          </div>
        </>
      )}

      {confirmDelete && <ConfirmModal title="Delete company" message="This company will be permanently removed." confirmLabel="Delete Company" onConfirm={deleteCompany} onCancel={() => setConfirmDelete(null)} />}
    </div>
  )
}

function Field({ label, children }) {
  return <div><label className="hv-form-label">{label}</label>{children}</div>
}
